'use client';

import { useLocale, useTranslations } from 'next-intl';
import { CalendarDays } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { getLocalizedPath } from '@/lib/locale-utils';
import { Card } from '@/components/ui/card';
import { Typography } from '@/components/ui/typography';
import { TextLink } from '@/components/ui/text-link';
import type { BlogPost } from '@/lib/api/blog';

interface BlogPostsGridProps {
    posts: BlogPost[];
    className?: string;
    limit?: number;
}

export function BlogPostsGrid({ posts, className, limit }: BlogPostsGridProps) {
    const locale = useLocale();
    const t = useTranslations('blog');
    const displayPosts = limit ? posts.slice(0, limit) : posts;

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? dateString : date.toLocaleDateString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    if (!displayPosts.length) {
        return (
            <div className={`text-center py-12 ${className || ''}`}>
                <p className="text-gray-500 dark:text-gray-400 text-lg">{t('noResults.title') || 'No posts found'}</p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">{t('noResults.message') || 'Check back soon for new blog posts.'}</p>
            </div>
        );
    }

    return (
        <div className={`grid gap-6 md:grid-cols-3 ${className || ''}`}>
            {displayPosts.map((post, index) => (
                <Card
                    key={post.slug}
                    title=""
                    description=""
                    animationDelay={index * 100}
                    className="border border-gray-200 dark:border-gray-800 !p-5"
                    renderContent={
                        <>
                            {post.image && (
                                <Link
                                    href={getLocalizedPath(post.slug, locale)}
                                    className="mb-5 block overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-900/90"
                                    aria-label={post.title}
                                >
                                    <Image
                                        src={post.image}
                                        alt={post.title}
                                        width={480}
                                        height={270}
                                        className="h-40 md:h-56 w-full object-cover opacity-95 transition-transform duration-500 group-hover:scale-105"
                                    />
                                </Link>
                            )}
                            {post.date && (
                                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-3">
                                    <CalendarDays className="h-4 w-4" />
                                    <span>{formatDate(post.date)}</span>
                                </div>
                            )}
                            <Typography variant="h4" className="mb-3">
                                {post.title}
                            </Typography>
                            {post.description && (
                                <Typography variant="p" className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
                                    {post.description}
                                </Typography>
                            )}
                            <div className="mt-2 flex items-center justify-between">
                                <TextLink href={getLocalizedPath(post.slug, locale)} ariaLabel={`Explore more about ${post.title}`}>
                                    {t('readMore') || 'Read more'}
                                </TextLink>
                            </div>
                        </>
                    }
                />
            ))}
        </div>
    );
}

